import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { KeycloakProvider, useKeycloak } from "../providers/keycloak-provider";
import { QueryProvider } from "../providers/query-provider";
import KcLoading from "../lib/kc-loading";
import HomePage from "../pages/HomePage";
import PontosPage from "../pages/PontosPage";
import FuncionariosPage from "../pages/FuncionariosPage";
import FeriadosPage from "../pages/FeriadosPage";
import DispositivoPage from "../pages/DispositivoPage";
import UsuariosPage from "../pages/UsuariosPage";

// Importa o serviço de polling automático do dispositivo
import "../services/dispositivoPollingService";

function AppRoutes() {
  const { isInit, error } = useKeycloak();

  // Exibe erro se a inicialização falhar
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-red-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-xl font-bold text-red-900">
              Erro de Autenticação
            </h2>
          </div>
          <div className="text-gray-700 mb-4">
            <p className="mb-2">
              Não foi possível conectar ao servidor Keycloak:
            </p>
            <p className="text-sm bg-red-50 p-3 rounded border border-red-200 font-mono">
              {error}
            </p>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            <p className="font-semibold mb-2">Possíveis causas:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Cliente "ponto-previ" não existe no Keycloak</li>
              <li>Cliente não está configurado como público (public)</li>
              <li>Redirect URI não está permitido</li>
              <li>Realm "Prefeitura" não existe</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!isInit) {
    return <KcLoading />;
  }

  const csrfToken =
    document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")
      ?.content ?? "";

  const common = {
    csrfToken,
    flash: {} as { alert?: string; notice?: string },
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={<HomePage uploadPath="/api/v1/upload" {...common} />}
      />
      <Route
        path="/pontos"
        element={<PontosPage dadosPath="/api/v1/pontos/dados" {...common} />}
      />
      <Route
        path="/funcionarios"
        element={
          <FuncionariosPage
            dadosPath="/api/v1/funcionarios/lista"
            {...common}
          />
        }
      />
      <Route
        path="/feriados"
        element={
          <FeriadosPage feriadosPath="/api/v1/admin/feriados" {...common} />
        }
      />
      <Route
        path="/dispositivo"
        element={
          <DispositivoPage
            statusPath="/api/v1/admin/dispositivos"
            syncPath="/api/v1/admin/dispositivos/sync"
            resetPath="/api/v1/admin/dispositivos/reset"
            configureWebhookPath="/api/v1/admin/dispositivos/configure_webhook"
            {...common}
          />
        }
      />
      <Route
        path="/usuarios"
        element={<UsuariosPage listPath="/api/v1/admin/usuarios" {...common} />}
      />
    </Routes>
  );
}

document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("app-root");
  if (!container || container.dataset.reactMounted) return;
  container.dataset.reactMounted = "true";

  createRoot(container).render(
    <KeycloakProvider>
      <QueryProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryProvider>
    </KeycloakProvider>,
  );
});
